import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  Users as UsersIcon, 
  Plus, 
  Mail, 
  Building2,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  UserCheck,
  UserX
} from 'lucide-react';
import { toast } from 'sonner';
import UserAvatar from '../components/UserAvatar';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [activeUserNames, setActiveUserNames] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'manager',
    store_id: ''
  });

  const isAdmin = ['owner', 'area_manager'].includes(user?.role);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (isAdmin) {
        // Admins get full user data
        const [usersRes, storesRes] = await Promise.all([
          axios.get(`${API_URL}/users`),
          axios.get(`${API_URL}/stores`)
        ]);
        setUsers(usersRes.data);
        setStores(storesRes.data);
      } else {
        // Non-admins only get active user names
        const namesRes = await axios.get(`${API_URL}/users/active-names`);
        setActiveUserNames(namesRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${API_URL}/auth/register`, formData);
      setUsers([...users, response.data.user]);
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'manager', store_id: '' });
      toast.success('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store?.name || 'Not assigned';
  };

  const getRoleBadge = (role) => {
    const styles = {
      owner: 'bg-purple-50 text-purple-700',
      area_manager: 'bg-indigo-50 text-indigo-700',
      manager: 'bg-blue-50 text-blue-700',
      supervisor: 'bg-green-50 text-green-700',
      employee: 'bg-slate-50 text-slate-700'
    };
    return styles[role] || 'bg-slate-50 text-slate-700';
  };

  const getRoleLabel = (role) => {
    const labels = {
      owner: 'Owner',
      area_manager: 'Area Manager',
      manager: 'Store Manager',
      supervisor: 'Supervisor',
      employee: 'Employee'
    };
    return labels[role] || role;
  };

  const getApprovalBadge = (approved) => {
    if (approved === true) {
      return 'bg-green-50 text-green-700';
    } else if (approved === false) {
      return 'bg-amber-50 text-amber-700';
    }
    return 'bg-slate-50 text-slate-700';
  };

  const [selectedRole, setSelectedRole] = useState({});
  const [selectedStore, setSelectedStore] = useState({});

  const handleApproveUser = async (userId, approve, role = null, storeId = null) => {
    try {
      const payload = { approved: approve };
      if (role) payload.role = role;
      if (storeId && storeId !== 'none') payload.store_id = storeId;
      
      await axios.put(`${API_URL}/users/${userId}/approve`, payload);
      setUsers(users.map(u => u.id === userId ? { ...u, approved: approve, role: role || u.role, store_id: payload.store_id || u.store_id } : u));
      toast.success(approve ? 'User approved — welcome email sent' : 'User rejected');
      // Clear selected role/store
      setSelectedRole(prev => ({ ...prev, [userId]: undefined }));
      setSelectedStore(prev => ({ ...prev, [userId]: undefined }));
    } catch (error) {
      console.error('Error updating user approval:', error);
      toast.error('Failed to update user approval');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API_URL}/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  // Separate pending and approved users
  const pendingUsers = users.filter(u => u.approved === false && u.role !== 'owner');
  const approvedUsers = users.filter(u => u.approved !== false || u.role === 'owner');

  // Non-admin view - show only active user names
  if (!isAdmin) {
    return (
      <div className="space-y-6" data-testid="users-page-limited">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Team Members
          </h1>
          <p className="text-slate-600 mt-1">
            Active team members
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500" data-testid="loading">
            Loading...
          </div>
        ) : activeUserNames.length === 0 ? (
          <Card className="bg-white border border-slate-200 rounded-sm">
            <CardContent className="py-12 text-center">
              <UsersIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-600">No team members found</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-white border border-slate-200 rounded-sm" data-testid="active-users-list">
            <CardHeader className="pb-2 border-b border-slate-100">
              <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <CheckCircle className="w-5 h-5 text-green-600" />
                Active Team ({activeUserNames.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-50">
                {activeUserNames.map((u) => (
                  <div key={u.id} className="flex items-center gap-3 py-3 px-6" data-testid={`user-name-${u.id}`}>
                    <UserAvatar name={u.name} picture={u.picture} size="md" />
                    <span className="font-medium text-slate-900">{u.name}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Admin view below

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Team Members
          </h1>
          <p className="text-slate-600 mt-1">
            Manage managers and supervisors across your stores
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Add New User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Smith"
                  className="rounded-sm"
                  required
                  data-testid="user-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@restaurant.com"
                  className="rounded-sm"
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Create a password"
                  className="rounded-sm"
                  required
                  data-testid="user-password-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="user-role-select">
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="store">Store</Label>
                  <Select 
                    value={formData.store_id} 
                    onValueChange={(value) => setFormData({ ...formData, store_id: value })}
                  >
                    <SelectTrigger className="rounded-sm" data-testid="user-store-select">
                      <SelectValue placeholder="Select store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.map((store) => (
                        <SelectItem key={store.id} value={store.id}>
                          {store.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
                  data-testid="save-user-btn"
                >
                  Add User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500" data-testid="loading">
          Loading users...
        </div>
      ) : users.length === 0 ? (
        <Card className="bg-white border border-slate-200 rounded-sm" data-testid="empty-state">
          <CardContent className="py-12 text-center">
            <UsersIcon className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              No team members yet
            </h3>
            <p className="text-slate-600 mb-6">
              Add managers and supervisors to your team
            </p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Team Member
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Pending Approvals Section */}
          {pendingUsers.length > 0 && (
            <Card className="bg-amber-50 border border-amber-200 rounded-sm" data-testid="pending-approvals">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-bold text-amber-800 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  <Clock className="w-5 h-5" />
                  Pending Approvals ({pendingUsers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {pendingUsers.map((u) => (
                    <div key={u.id} className="flex items-center justify-between p-4 bg-white rounded-sm border border-amber-200" data-testid={`pending-user-${u.id}`}>
                      <div className="flex items-center gap-4">
                        <UserAvatar name={u.name} picture={u.picture} size="md" />
                        <div>
                          <p className="font-medium text-slate-900">{u.name}</p>
                          <p className="text-sm text-slate-500">{u.email}</p>
                          {u.store_name && <p className="text-xs text-orange-600">{u.store_name}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select 
                          value={selectedRole[u.id] || u.role} 
                          onValueChange={(value) => setSelectedRole(prev => ({ ...prev, [u.id]: value }))}
                        >
                          <SelectTrigger className="w-36 h-8 text-sm" data-testid={`pending-role-${u.id}`}>
                            <SelectValue placeholder="Role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="area_manager">Area Manager</SelectItem>
                            <SelectItem value="manager">Store Manager</SelectItem>
                            <SelectItem value="supervisor">Supervisor</SelectItem>
                            <SelectItem value="employee">Employee</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select
                          value={selectedStore[u.id] || u.store_id || 'none'}
                          onValueChange={(value) => setSelectedStore(prev => ({ ...prev, [u.id]: value }))}
                        >
                          <SelectTrigger className="w-44 h-8 text-sm" data-testid={`pending-store-${u.id}`}>
                            <SelectValue placeholder="Store" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">All stores / none</SelectItem>
                            {stores.map((s) => (
                              <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => handleApproveUser(u.id, true, selectedRole[u.id] || u.role, selectedStore[u.id] || u.store_id)}
                          data-testid={`approve-user-${u.id}`}
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => handleDeleteUser(u.id)}
                          data-testid={`reject-user-${u.id}`}
                        >
                          <UserX className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Active Users Table */}
          <Card className="bg-white border border-slate-200 rounded-sm" data-testid="users-table">
            <CardHeader className="pb-2 border-b border-slate-100">
              <CardTitle className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <CheckCircle className="w-5 h-5 text-green-600" />
                Active Team Members ({approvedUsers.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wide">User</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wide">Store</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedUsers.map((u) => (
                      <tr key={u.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`user-${u.id}`}>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <UserAvatar name={u.name} picture={u.picture} size="md" />
                            <span className="font-medium text-slate-900">{u.name}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2 text-slate-600">
                            <Mail className="w-4 h-4" />
                            {u.email}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-sm font-medium capitalize ${getRoleBadge(u.role)}`}>
                            <Shield className="w-3 h-3 mr-1" />
                            {u.role}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-2 text-slate-600">
                            <Building2 className="w-4 h-4" />
                            {getStoreName(u.store_id)}
                          </div>
                        </td>
                        <td className="py-4 px-6">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-sm text-sm font-medium ${getApprovalBadge(u.approved)}`}>
                            {u.approved !== false || u.role === 'owner' ? (
                              <>
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Active
                              </>
                            ) : (
                              <>
                                <Clock className="w-3 h-3 mr-1" />
                                Pending
                              </>
                            )}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          {u.role !== 'owner' && u.id !== user?.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleDeleteUser(u.id)}
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
